/**
 * GHL-style workflow step card visual tokens.
 */

export const WORKFLOW_STEP_STYLE_PRESETS = {
  trigger: {
    card: 'bg-[#F0FDF4] border-[#BBF7D0]',
    icon: 'bg-[#DCFCE7] text-[#059669]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  email: {
    card: 'bg-[#F8FBFF] border-[#BFDBFE]',
    icon: 'bg-[#DBEAFE] text-[#2563EB]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  sms: {
    card: 'bg-[#F0FDFA] border-[#99F6E4]',
    icon: 'bg-[#CCFBF1] text-[#059669]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  notification: {
    card: 'bg-[#F0FDF4] border-[#BBF7D0]',
    icon: 'bg-[#DCFCE7] text-[#047857]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  payment: {
    card: 'bg-[#F5F3FF] border-[#C4B5FD]',
    icon: 'bg-[#EDE9FE] text-[#7C3AED]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  backend: {
    card: 'bg-[#FFF7ED] border-[#FDBA74]',
    icon: 'bg-[#FFEDD5] text-[#F97316]',
    title: 'text-slate-900',
    description: 'text-slate-600',
    badge: 'bg-[#FFEDD5] text-[#EA580C] border-[#FDBA74]',
  },
  wait: {
    card: 'bg-[#FFFBEB] border-[#FCD34D]',
    icon: 'bg-[#FEF3C7] text-[#F59E0B]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  condition: {
    card: 'bg-[#F5F3FF] border-[#C4B5FD]',
    icon: 'bg-[#EDE9FE] text-[#7C3AED]',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
  default: {
    card: 'bg-white border-slate-300',
    icon: 'bg-slate-100 text-slate-600',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
};

const EMAIL_TYPES = new Set(['send_email', 'email_action', 'send_portal_invite']);
const SMS_TYPES = new Set(['send_sms']);
const NOTIFICATION_TYPES = new Set(['send_notification', 'internal_notification', 'staff_alert']);
const PAYMENT_TYPES = new Set(['create_opportunity', 'move_opportunity_stage', 'grant_offer_access', 'revoke_offer_access']);
const BACKEND_TYPES = new Set(['backend_action', 'action', 'update_record', 'update_status', 'audit_log', 'security_lock', 'manual_review', 'webhook']);
const WAIT_TYPES = new Set(['wait', 'delay', 'wait_until']);
const CONDITION_TYPES = new Set(['condition', 'if_else', 'branch']);

export function getWorkflowStepStylePreset(step) {
  const type = step?.type;
  if (type === 'trigger') return WORKFLOW_STEP_STYLE_PRESETS.trigger;
  if (EMAIL_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.email;
  if (SMS_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.sms;
  if (NOTIFICATION_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.notification;
  if (PAYMENT_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.payment;
  if (WAIT_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.wait;
  if (CONDITION_TYPES.has(type)) return WORKFLOW_STEP_STYLE_PRESETS.condition;
  if (BACKEND_TYPES.has(type) || step?.config?.requires_backend) {
    return WORKFLOW_STEP_STYLE_PRESETS.backend;
  }
  return WORKFLOW_STEP_STYLE_PRESETS.default;
}

export function getWorkflowStepCardClasses({
  step,
  isSelected = false,
  isMoving = false,
  highlighted = false,
  pathState = null,
  hasChannelIssue = false,
}) {
  const preset = getWorkflowStepStylePreset(step);
  const selected = isSelected || isMoving || highlighted;

  return {
    card: [
      'group flex min-h-[76px] w-full max-w-[430px] items-center gap-3 rounded-[18px] border px-[18px] py-4 shadow-sm transition-all',
      hasChannelIssue ? 'border-red-300 bg-red-50' : preset.card,
      selected && 'border-emerald-600 ring-2 ring-emerald-600/15',
      pathState === 'active' && 'border-emerald-500 ring-2 ring-emerald-500/20',
      pathState === 'completed' && 'border-emerald-400',
      !hasChannelIssue && 'hover:shadow-md',
    ].filter(Boolean).join(' '),
    icon: hasChannelIssue ? 'bg-red-100 text-red-600' : preset.icon,
    title: preset.title,
    description: preset.description,
    badge: preset.badge || 'bg-[#FFEDD5] text-[#EA580C] border-[#FDBA74]',
  };
}

export const WORKFLOW_TRIGGER_CARD_CLASSES = {
  card: 'bg-white border-slate-200 rounded-2xl border shadow-sm',
  icon: 'bg-[#DCFCE7] text-[#059669]',
  title: 'text-slate-900 font-semibold text-[15px] leading-snug',
  subtitle: 'text-slate-500 text-sm',
  actionRow: 'border-t border-slate-100',
};

export const WORKFLOW_ADD_TRIGGER_PLACEHOLDER_CLASSES = {
  card: 'min-h-[132px] w-[340px] rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 px-4 py-5 shadow-sm',
  icon: 'bg-white text-emerald-600 shadow-sm border border-emerald-100',
  title: 'text-emerald-700 font-semibold text-sm',
};
